<script setup>
import { ref, computed, provide } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useDealsStore } from 'dashboard/stores/deals';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealCreateSidebar from 'dashboard/components-next/Deals/DealDetail/DealCreateSidebar.vue';
import DealDetailSidebar from 'dashboard/components-next/Deals/DealDetail/DealDetailSidebar.vue';
import ContactDetailSidebar from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailSidebar.vue';
import CompanyDetailSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyDetailSidebar.vue';

// Coluna esquerda das paginas de Contato, Empresa e Negociacao: o conteudo
// principal (slot) fica vivo e escondido enquanto um Contato/Empresa/Negociacao
// vinculado e aberto aqui mesmo, sem trocar de rota. A seta de voltar de
// cada nivel desempilha um por vez. Conversas abrem no painel da direita
// (a pagina dona decide o que mostrar la via eventos).
const props = defineProps({
  activeConversationId: { type: Number, default: null },
});

const emit = defineEmits([
  'openConversation',
  'closeConversation',
  'linkedUpdated',
]);

const store = useStore();
const companiesStore = useCompaniesStore();
const dealsStore = useDealsStore();
const getContactById = useMapGetter('contacts/getContactById');

const stack = ref([]);
const current = computed(() => stack.value[stack.value.length - 1] || null);
const linkedContact = computed(() =>
  current.value?.type === 'contact'
    ? getContactById.value(current.value.id)
    : null
);
const linkedDeal = computed(() =>
  current.value?.type === 'deal' ? current.value.deal : null
);
const isCreatingDeal = computed(() => current.value?.type === 'deal-new');
// Listas do conteudo principal (ex.: Negociacoes do contato) recarregam
// quando algo muda/volta dos niveis abertos aqui.
const linkedRevision = ref(0);
const linkedCompany = computed(() =>
  current.value?.type === 'company'
    ? companiesStore.getCompanyById(current.value.id)
    : null
);

const openLinked = async (type, id) => {
  emit('closeConversation');
  stack.value.push({ type, id, deal: null });
  const entry = stack.value[stack.value.length - 1];
  if (type === 'contact') store.dispatch('contacts/show', { id });
  else if (type === 'company') companiesStore.show(id);
  else entry.deal = await dealsStore.show(id);
};

const goBack = () => {
  emit('closeConversation');
  stack.value.pop();
  linkedRevision.value += 1;
};

// Nova negociacao na propria coluna esquerda (contato/empresa ja preenchidos).
// Criada, o mesmo nivel passa a mostrar a negociacao.
const openNewDeal = ({ contactId, companyId, conversationId } = {}) => {
  emit('closeConversation');
  stack.value.push({
    type: 'deal-new',
    initialValues: { contactId, companyId },
    conversationId: conversationId || null,
  });
};

const handleDealCreated = async created => {
  const deal = await dealsStore.show(created.id);
  stack.value[stack.value.length - 1] = { type: 'deal', id: created.id, deal };
  linkedRevision.value += 1;
  emit('linkedUpdated');
};

const handleDealUpdated = updated => {
  current.value.deal = updated;
  linkedRevision.value += 1;
  emit('linkedUpdated');
};

// Negociacao excluida: nada mais a mostrar neste nivel
const handleDealDeleted = () => {
  goBack();
  emit('linkedUpdated');
};

// Usados por qualquer descendente (cards de conversa, secoes de empresa/contato)
provide('openLinkedInPlace', openLinked);
provide('linkedRevision', linkedRevision);
provide('openNewDealInPlace', openNewDeal);
provide('openConversationInPlace', id => emit('openConversation', id));
provide(
  'activeConversationId',
  computed(() => props.activeConversationId)
);
</script>

<template>
  <div v-show="!current" class="h-full">
    <slot />
  </div>
  <DealCreateSidebar
    v-if="isCreatingDeal"
    :initial-values="current.initialValues"
    :conversation-id="current.conversationId"
    @back="goBack"
    @created="handleDealCreated"
  />
  <DealDetailSidebar
    v-else-if="linkedDeal?.id"
    :key="`deal-${linkedDeal.id}`"
    :deal="linkedDeal"
    :is-updating="dealsStore.uiFlags.updatingItem"
    @back="goBack"
    @updated="handleDealUpdated"
    @deleted="handleDealDeleted"
  />
  <ContactDetailSidebar
    v-else-if="linkedContact?.id"
    :key="`contact-${linkedContact.id}`"
    :contact="linkedContact"
    @back="goBack"
    @updated="emit('linkedUpdated')"
  />
  <CompanyDetailSidebar
    v-else-if="linkedCompany?.id"
    :key="`company-${linkedCompany.id}`"
    :company="linkedCompany"
    @back="goBack"
    @updated="emit('linkedUpdated')"
  />
  <div
    v-else-if="current"
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
